import { useClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/useClientSideDataGridModel";
import { PartConfigurationApi } from "src/api/generated/erp/configurator/api/partConfigurationApi.ts";
import { ControlledAsyncDataGrid } from "src/components/common/dataGrid/ControlledAsyncDataGrid.tsx";
import { PartConfigurationProperty } from "src/api/generated/erp/db/types/tables/partConfigurationProperty";
import i18n from "i18next";
import { isEmptyConfigurationPropertyValue } from "src/components/views/erp/configurator/configuratorUtils.ts";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface PartConfigurationPropertiesDataGridProps {
	partConfigurationId: number;
}

export const PartConfigurationPropertiesDataGrid = ({
	partConfigurationId,
}: PartConfigurationPropertiesDataGridProps) => {
	const { dataGridProps, refreshData } = useClientSideDataGridModel({
		customFunctionUiActionView: "PART_CONFIGURATION_PROPERTIES",
		fetchData: async ({ searchQuery }) => {
			const allProps = await PartConfigurationApi.getPartConfigurationProperties({
				partConfigurationId,
				searchQuery,
			});
			return allProps.filter(
				(prop) => !(prop.hideIfEmptyValue && isEmptyConfigurationPropertyValue(prop.propertyValue)),
			);
		},
		initialParams: {
			searchQuery: "",
		},
		getRowId: (row) => row.partConfigurationPropertyId,
		gridId: "CBAF40A175B53A68",
	});

	return (
		<ControlledAsyncDataGrid<PartConfigurationProperty>
			hideRefreshButton
			columns={[
				{
					type: "string",
					field: "propertyTitle",
					headerName: i18n.t("property"),
					width: 300,
				},
				{
					type: "string",
					field: "propertyValue",
					headerName: i18n.t("value"),
					valueGetter: (_, row) => row.propertyValue?.label ?? "-",
					width: 700,
				},
			]}
			actionBarComponents={
				<>
					<AavoTextField label={i18n.t("search")} onSubmit={(value) => refreshData({ searchQuery: value })} />
				</>
			}
			agentContext={{
				key: defineAgentUiContextKey("erp.configurator.partConfigurationProperties.grid"),
				data: {
					partConfigurationId: partConfigurationId,
				},
				rowFields: ["partConfigurationPropertyId", "partConfigurationId", "propertyName"],
				selectedRowPathSegment: (row) => row.propertyTitle,
			}}
			{...dataGridProps}
		/>
	);
};
